import { useState, useEffect } from 'react';
import { api } from '../../../core/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { AlertCircle, Save, RotateCcw, Hash, Calendar } from 'lucide-react';

interface BrandCounter {
  id: string;
  code: string;
  name: string;
  companyId: number;
  invoicePrefix: string;
  nextInvoiceNumber: number;
  projectPrefix: string;
  nextProjectNumber: number;
  workOrderPrefix: string;
  nextWorkOrderNumber: number;
  purchaseOrderPrefix: string;
  nextPurchaseOrderNumber: number;
}

interface PurchaseOrderInfo {
  year: number;
  totalThisYear: number;
  nextNumber: number;
  format: string;
}

interface CounterData {
  brands: BrandCounter[];
  purchaseOrders: PurchaseOrderInfo;
}

type EditState = Record<string, Partial<BrandCounter>>;

export default function Counters() {
  const [data, setData] = useState<CounterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [edits, setEdits] = useState<EditState>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [saved, setSaved] = useState<Record<string, boolean>>({});

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/counters');
      setData((res as any).data);
      setEdits({});
    } catch {
      setError('Error en carregar els contadors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const getEdit = (brandId: string, brand: BrandCounter) => ({
    ...brand,
    ...edits[brandId],
  });

  const setField = (brandId: string, field: keyof BrandCounter, value: string | number) => {
    setEdits((prev) => ({
      ...prev,
      [brandId]: { ...prev[brandId], [field]: value },
    }));
    setSaved((prev) => ({ ...prev, [brandId]: false }));
  };

  const hasChanges = (brandId: string) => Object.keys(edits[brandId] || {}).length > 0;

  const saveBrand = async (brand: BrandCounter) => {
    if (!hasChanges(brand.id)) return;
    setSaving((prev) => ({ ...prev, [brand.id]: true }));
    try {
      const patch = edits[brand.id];
      await api.put(`/admin/counters/brands/${brand.id}`, patch);
      setSaved((prev) => ({ ...prev, [brand.id]: true }));
      setEdits((prev) => {
        const next = { ...prev };
        delete next[brand.id];
        return next;
      });
      await load();
    } catch {
      setError(`Error en desar els contadors de ${brand.name}`);
    } finally {
      setSaving((prev) => ({ ...prev, [brand.id]: false }));
    }
  };

  const discardBrand = (brandId: string) => {
    setEdits((prev) => {
      const next = { ...prev };
      delete next[brandId];
      return next;
    });
    setSaved((prev) => ({ ...prev, [brandId]: false }));
  };

  const counterRows = [
    {
      key: 'invoice',
      label: 'Factures',
      prefixField: 'invoicePrefix' as keyof BrandCounter,
      counterField: 'nextInvoiceNumber' as keyof BrandCounter,
    },
    {
      key: 'project',
      label: 'Expedients',
      prefixField: 'projectPrefix' as keyof BrandCounter,
      counterField: 'nextProjectNumber' as keyof BrandCounter,
    },
    {
      key: 'workOrder',
      label: 'Ordres de treball',
      prefixField: 'workOrderPrefix' as keyof BrandCounter,
      counterField: 'nextWorkOrderNumber' as keyof BrandCounter,
    },
    {
      key: 'purchaseOrder',
      label: 'Comandes (intern)',
      prefixField: 'purchaseOrderPrefix' as keyof BrandCounter,
      counterField: 'nextPurchaseOrderNumber' as keyof BrandCounter,
    },
  ] as const;

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Contadors i Numeració</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Gestiona els prefixos i el pròxim número de cada sèrie documental per marca.
          </p>
        </div>
        <Button variant="outline" onClick={load} className="gap-2">
          <RotateCcw className="w-4 h-4" />
          Recarregar
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* PO info (autonomous counter) */}
      {data?.purchaseOrders && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-xs flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-600" />
              Comandes a proveïdors — Exercici {data.purchaseOrders.year}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-6 text-sm">
              <div>
                <span className="text-muted-foreground">Format</span>
                <p className="font-mono font-semibold">{data.purchaseOrders.format}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Emeses aquest any</span>
                <p className="font-bold text-sm">{data.purchaseOrders.totalThisYear}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Pròxima</span>
                <p className="font-bold text-sm text-blue-700">
                  PO-{data.purchaseOrders.year}-{String(data.purchaseOrders.nextNumber).padStart(5, '0')}
                </p>
              </div>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Aquest comptador és autònom (basat en el recompte real de la BD) i es reinicia automàticament cada any natural. No es pot modificar manualment.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Brand counters */}
      {data?.brands.map((brand) => {
        const e = getEdit(brand.id, brand);
        const dirty = hasChanges(brand.id);
        const isSaving = saving[brand.id];

        return (
          <Card key={brand.id}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xs flex items-center gap-2">
                  <Hash className="w-4 h-4 text-primary" />
                  {brand.name}
                  <Badge variant="outline" className="text-xs font-mono">{brand.code}</Badge>
                </CardTitle>
                <div className="flex gap-2">
                  {dirty && (
                    <Button variant="outline" size="sm" onClick={() => discardBrand(brand.id)} disabled={isSaving}>
                      <RotateCcw className="w-3.5 h-3.5 mr-1" />
                      Descartar
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => saveBrand(brand)}
                    disabled={!dirty || isSaving}
                    className="gap-1.5"
                  >
                    <Save className="w-3.5 h-3.5" />
                    {isSaving ? 'Desant...' : saved[brand.id] ? 'Desat!' : 'Desar'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-muted-foreground text-xs">
                      <th className="text-left py-2 pr-4 font-medium w-40">Document</th>
                      <th className="text-left py-2 pr-4 font-medium w-32">Prefix</th>
                      <th className="text-left py-2 pr-4 font-medium w-36">Pròxim núm.</th>
                      <th className="text-left py-2 font-medium">Exemple</th>
                    </tr>
                  </thead>
                  <tbody>
                    {counterRows.map(({ key, label, prefixField, counterField }) => {
                      const prefix = String(e[prefixField] ?? '');
                      const next = Number(e[counterField] ?? 1);
                      const example = `${prefix}-${new Date().getFullYear()}-${String(next).padStart(5, '0')}`;
                      return (
                        <tr key={key} className="border-b last:border-0">
                          <td className="py-3 pr-4 font-medium">{label}</td>
                          <td className="py-3 pr-4">
                            <Input
                              value={prefix}
                              onChange={(ev) => setField(brand.id, prefixField, ev.target.value.toUpperCase())}
                              maxLength={10}
                              className="h-8 w-24 font-mono uppercase"
                            />
                          </td>
                          <td className="py-3 pr-4">
                            <Input
                              type="number"
                              min={1}
                              value={next}
                              onChange={(ev) => setField(brand.id, counterField, parseInt(ev.target.value) || 1)}
                              className="h-8 w-28"
                            />
                          </td>
                          <td className="py-3 font-mono text-muted-foreground text-xs">
                            {example}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
